/** biome-ignore-all lint/a11y/useSemanticElements: each side is a read-only labelled group of facts, not form controls, so <fieldset>/<legend> would be the wrong semantics (and its default rendering would fight the card layout). */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraEyebrow } from '../ensigns/eyebrow';
import { SpecraText } from '../ensigns/typography';
import { SpecraCard } from '../lieutenants/card';
import { SpecraKeyValueRow } from '../lieutenants/key-value-row';
const DEFAULT_STATE_LABELS = {
    different: 'different',
    same: 'same'
};
function CompareSide({ framed, getRowTestId, side, stateLabels }) {
    const eyebrowId = React.useId();
    const content = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                id: eyebrowId,
                children: side.eyebrow
            }),
            side.title ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "div",
                className: "mt-[var(--space-1)] font-medium",
                size: "body-sm",
                children: side.title
            }) : null,
            side.body ? /*#__PURE__*/ _jsx("div", {
                className: "mt-[var(--space-2)]",
                children: side.body
            }) : null,
            side.rows?.map((row)=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                    "data-item-id": row.id,
                    "data-testid": getRowTestId?.(side, row),
                    label: row.label,
                    value: /*#__PURE__*/ _jsxs("span", {
                        className: cn(row.state === 'same' && 'font-medium text-foreground', row.state === 'different' && 'font-medium text-danger'),
                        children: [
                            row.value,
                            row.state && row.state !== 'neutral' ? ` · ${stateLabels[row.state]}` : null
                        ]
                    })
                }, row.id)),
            side.footer ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                className: "mt-[var(--space-2)]",
                size: "meta",
                tone: "muted",
                children: side.footer
            }) : null
        ]
    });
    if (framed === 'panel') {
        return /*#__PURE__*/ _jsx("div", {
            "aria-labelledby": eyebrowId,
            className: "p-[var(--space-4)] md:[&+&]:border-l md:[&+&]:border-border",
            "data-item-id": side.id,
            role: "group",
            children: content
        });
    }
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "aria-labelledby": eyebrowId,
        "data-item-id": side.id,
        padding: "sm",
        radius: "lg",
        role: "group",
        children: content
    });
}
/** Two sides compared row by row ("As written" vs "Anchor"), same/different stated in words. */ export const SpecraCompareColumns = /*#__PURE__*/ React.forwardRef(({ className, framed = 'cards', getRowTestId, sides, stateLabels = DEFAULT_STATE_LABELS, ...props }, ref)=>{
    const rendered = sides.map((side)=>/*#__PURE__*/ _jsx(CompareSide, {
            framed: framed,
            getRowTestId: getRowTestId,
            side: side,
            stateLabels: stateLabels
        }, side.id));
    if (framed === 'panel') {
        return /*#__PURE__*/ _jsx(SpecraCard, {
            className: cn('grid md:grid-cols-2', className),
            "data-framed": "panel",
            padding: "none",
            radius: "lg",
            ref: ref,
            ...props,
            children: rendered
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: cn('grid gap-[var(--space-3)] md:grid-cols-2', className),
        "data-framed": "cards",
        ref: ref,
        ...props,
        children: rendered
    });
});
SpecraCompareColumns.displayName = 'SpecraCompareColumns';
